/**
 * BookingStatsReport – aggregerad bokningsstatistik (REPORTS_BOOKING_STATS).
 * Volym per status, request-flöde, beläggning, snittlängd, unika bokare,
 * körsträcka, fördelning per bilklass, topplista och trend.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import { useAuthStore } from '@/store/authStore';
import { bookingApi, type BookingStats, type PickupLocation } from '@/services/booking/bookingApi';
import { organizationsService } from '@/services/organizations/organizationsService';
import type { Organization } from '@/types/organizations';

import DateInput from '@/components/common/DateInput';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function Kpi({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-bgElevated rounded-lg border border-border p-4">
      <p className="text-xs text-textSecondary uppercase">{label}</p>
      <p className="text-2xl font-bold text-textDefault mt-1">{value}</p>
      {sub && <p className="text-xs text-textSecondary mt-1">{sub}</p>}
    </div>
  );
}

function Bars({ data }: { data: { label: string; value: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="space-y-1">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-2">
          <span className="w-32 shrink-0 text-xs text-textSecondary truncate" title={d.label}>{d.label}</span>
          <div className="flex-1 bg-bgBase rounded h-4 overflow-hidden">
            <div className="bg-primary h-4" style={{ width: `${(d.value / max) * 100}%` }} />
          </div>
          <span className="w-12 shrink-0 text-right text-xs text-textDefault">{d.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function BookingStatsReport() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isGlobalAdmin = user?.is_global_admin === true;

  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('week');
  const [basis, setBasis] = useState<'calendar' | 'workdays'>('calendar');
  const [organizationId, setOrganizationId] = useState<string>('');
  const [pickupLocationId, setPickupLocationId] = useState<string>('');

  const [topNodes, setTopNodes] = useState<Organization[]>([]);
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [data, setData] = useState<BookingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isGlobalAdmin) {
      organizationsService.getTopNodes().then(setTopNodes).catch(() => setTopNodes([]));
    }
    bookingApi.getPickupLocations().then(setPickupLocations).catch(() => setPickupLocations([]));
  }, [isGlobalAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingApi.getBookingStats({
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
        granularity,
        basis,
        ...(organizationId ? { organizationId } : {}),
        ...(pickupLocationId ? { pickupLocationId } : {}),
      });
      setData(result);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('reports.bookingStats.loadError'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, granularity, basis, organizationId, pickupLocationId, t]);

  useEffect(() => { void load(); }, [load]);

  // Kärnstatusar visas alltid (även med 0) så att t.ex. NO_SHOW syns även innan
  // någon no-show inträffat. Övriga statusar (t.ex. CANCELLED_BY_SYSTEM) visas bara om de förekommer.
  const statusBars = useMemo(() => {
    if (!data) return [];
    const by = data.summary.byStatus;
    const coreOrder = ['BOOKED', 'CHECKED_OUT', 'RETURNED', 'NO_SHOW', 'CANCELLED'];
    const core = coreOrder.map((label) => ({ label, value: by[label] ?? 0 }));
    const extra = Object.entries(by)
      .filter(([label]) => !coreOrder.includes(label))
      .map(([label, value]) => ({ label, value }));
    return [...core, ...extra];
  }, [data]);
  const carClassBars = useMemo(
    () => (data ? data.byCarClass.map((c) => ({ label: c.carClass, value: c.count })) : []),
    [data]
  );
  const trendBars = useMemo(
    () => (data ? data.trend.map((trd) => ({ label: trd.period, value: trd.count })) : []),
    [data]
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <PageBackButton />
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <ChartBarIcon className="w-6 h-6 text-primary" />
          {t('reports.bookingStats.title')}
        </h1>
      </div>
      <p className="text-sm text-textSecondary mb-6">{t('reports.bookingStats.description')}</p>

      <div className="bg-bgElevated rounded-lg border border-border p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-textSecondary mb-1">{t('reports.bookingStats.from')}</label>
          <DateInput value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 border rounded bg-bgBase text-textDefault" />
        </div>
        <div>
          <label className="block text-xs text-textSecondary mb-1">{t('reports.bookingStats.to')}</label>
          <DateInput value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 border rounded bg-bgBase text-textDefault" />
        </div>
        <div>
          <label className="block text-xs text-textSecondary mb-1">{t('reports.bookingStats.granularity')}</label>
          <select value={granularity} onChange={(e) => setGranularity(e.target.value as typeof granularity)} className="px-3 py-2 border rounded bg-bgBase text-textDefault">
            <option value="day">{t('reports.bookingStats.granularityDay')}</option>
            <option value="week">{t('reports.bookingStats.granularityWeek')}</option>
            <option value="month">{t('reports.bookingStats.granularityMonth')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-textSecondary mb-1">{t('reports.bookingStats.basis')}</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value as typeof basis)} className="px-3 py-2 border rounded bg-bgBase text-textDefault">
            <option value="calendar">{t('reports.bookingStats.basisCalendar')}</option>
            <option value="workdays">{t('reports.bookingStats.basisWorkdays')}</option>
          </select>
        </div>
        {isGlobalAdmin && topNodes.length > 0 && (
          <div>
            <label className="block text-xs text-textSecondary mb-1">{t('booking.organization')}</label>
            <select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} className="px-3 py-2 border rounded bg-bgBase text-textDefault">
              <option value="">{t('reports.bookingStats.allOrganizations')}</option>
              {topNodes.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs text-textSecondary mb-1">{t('reports.bookingStats.pickupLocation')}</label>
          <select value={pickupLocationId} onChange={(e) => setPickupLocationId(e.target.value)} className="px-3 py-2 border rounded bg-bgBase text-textDefault">
            <option value="">{t('reports.bookingStats.allPickupLocations')}</option>
            {pickupLocations.map((pl) => (
              <option key={pl.id} value={pl.id}>{pl.name}</option>
            ))}
          </select>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading} className="px-4 py-2 bg-primary text-white rounded disabled:opacity-50">
          {loading ? t('common.loading') : t('reports.bookingStats.refresh')}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi label={t('reports.bookingStats.totalBookings')} value={data.summary.totalBookings} />
            <Kpi
              label={t('reports.bookingStats.requestFlow')}
              value={`${data.summary.totalRequests} / ${data.summary.totalAssigned}`}
              sub={t('reports.bookingStats.requestSharePct', { pct: data.summary.requestSharePct })}
            />
            <Kpi label={t('reports.bookingStats.occupancy')} value={`${data.occupancy.occupancyPct}%`} sub={`${data.occupancy.bookedHours} / ${data.occupancy.capacityHours} h`} />
            <Kpi label={t('reports.bookingStats.avgLength')} value={`${data.summary.avgLengthHours} h`} />
            <Kpi
              label={t('reports.bookingStats.uniqueBookers')}
              value={data.summary.uniqueBookers}
              sub={t('reports.bookingStats.uniqueBookersSub')}
            />
            <Kpi label={t('reports.bookingStats.totalKm')} value={data.summary.totalEstimatedKm} />
            <Kpi label={t('reports.bookingStats.vehicleCount')} value={data.occupancy.vehicleCount} />
            <Kpi
              label={t('reports.bookingStats.noShow')}
              value={data.summary.noShowCount}
              sub={t('reports.bookingStats.noShowSub')}
            />
            <Kpi
              label={t('reports.bookingStats.serviceNotUsed')}
              value={`${data.summary.serviceNotUsedCount} / ${data.summary.serviceTotalCount}`}
              sub={t('reports.bookingStats.serviceNotUsedSub', { pct: data.summary.serviceNotUsedPct })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <h2 className="text-sm font-semibold text-textDefault mb-3">{t('reports.bookingStats.byStatus')}</h2>
              {statusBars.length ? <Bars data={statusBars} /> : <p className="text-sm text-textSecondary">{t('reports.bookingStats.noData')}</p>}
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <h2 className="text-sm font-semibold text-textDefault mb-3">{t('reports.bookingStats.byCarClass')}</h2>
              {carClassBars.length ? <Bars data={carClassBars} /> : <p className="text-sm text-textSecondary">{t('reports.bookingStats.noData')}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <h2 className="text-sm font-semibold text-textDefault mb-3">{t('reports.bookingStats.trend')}</h2>
              {trendBars.length ? <Bars data={trendBars} /> : <p className="text-sm text-textSecondary">{t('reports.bookingStats.noData')}</p>}
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <h2 className="text-sm font-semibold text-textDefault mb-3">{t('reports.bookingStats.topVehicles')}</h2>
              {data.topVehicles.length ? (
                <table className="w-full text-sm">
                  <tbody>
                    {data.topVehicles.map((v) => (
                      <tr key={`${v.vehicleId}-${v.registrationNumber}`} className="border-b border-border last:border-0">
                        <td className="py-1 text-textDefault">{v.registrationNumber || v.vehicleId || '—'}</td>
                        <td className="py-1 text-right text-textSecondary">{v.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-textSecondary">{t('reports.bookingStats.noData')}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
